/**
 * Session Progress Utility
 * Calculates progress percentage based on phase and subphase
 * 
 * Structure:
 * - Phase 1: Subphases 1.1, 1.2, 1.3, 1.4
 * - Phase 2: Subphase 2.0
 * - Phase 3: Subphases 3.0, 3.1, 3.2, 3.3, 3.4 (Report)
 */

const SUBPHASE_PROGRESS: Record<string, number> = {
  // Phase 1 - Problem verstehen & Optionen entwickeln
  '1.1': 10,   // Problem verstehen
  '1.2': 20,   // Spannungsfeld erkunden
  '1.3': 30,   // Hypothese formulieren
  '1.4': 40,   // Entscheidungsfokus
  
  // Phase 2 - Optionen analysieren
  '2.0': 55,   // Optionen entwickeln & analysieren
  
  // Phase 3 - Bewertung & Report
  '3.0': 65,   // Bewertung starten
  '3.1': 75,   // Intro & Übersicht
  '3.2': 82,   // Matrix & Vergleich
  '3.3': 90,   // Dominante Strategie
  '3.4': 100,  // Report fertig
};

const SUBPHASE_LABELS: Record<string, string> = {
  '1.1': 'Entscheidungsfokus',
  '1.2': 'Spannungsfeld',
  '1.3': 'Kernannahme',
  '1.4': 'Synthese',
  '2.0': 'Optionen analysieren',
  '3.0': 'Bewertung starten',
  '3.1': 'Intro & Übersicht',
  '3.2': 'Matrix & Vergleich',
  '3.3': 'Dominante Strategie',
  '3.4': 'Report',
};

export interface SessionProgressInfo {
  progress: number;
  label: string;
  phase: number;
}

/**
 * Get session progress based on subphase
 */
export function getSessionProgress(subphase: string): number {
  return SUBPHASE_PROGRESS[subphase] ?? 0;
}

/**
 * Get subphase label for tooltip/display
 */
export function getSubphaseLabel(subphase: string): string {
  return SUBPHASE_LABELS[subphase] ?? 'In Bearbeitung';
}

/**
 * Get phase number from subphase string (e.g., "1.2" → 1)
 */
export function getPhaseFromSubphase(subphase: string): number {
  const phase = parseInt(subphase.split('.')[0], 10);
  return isNaN(phase) ? 1 : phase;
}

/**
 * Get complete progress info for a session
 */
export function getSessionProgressInfo(subphase: string): SessionProgressInfo {
  const phase = getPhaseFromSubphase(subphase);
  const progress = getSessionProgress(subphase);
  const label = getSubphaseLabel(subphase);
  
  return {
    progress,
    label,
    phase,
  };
}
